import { type Driver, type DriveStep } from 'driver.js'
import type { TFunction } from 'i18next'

import { useSettingsStore } from '~/stores/settings'

import { cardsTourSelectors } from './selectors'

export const createCardsTourSteps = ({
  driverObj,
  onTourCompleted,
  t,
}: {
  driverObj: Driver
  onTourCompleted: () => void
  t: TFunction<['onboarding', 'translation']>
}) => {
  const { advanceTourProgress, completeTourProgress } = useSettingsStore.getState()
  const editAriaLabel = t('edit current card', { ns: 'translation' })
  const refreshAriaLabel = t('refresh stack', { ns: 'translation' })
  const reverseAriaLabel = t('reverse front side text', { ns: 'translation' })
  const deleteAriaLabel = t('delete active card', { ns: 'translation' })

  const persistStep = (stepIndex: number) => {
    advanceTourProgress('cards', stepIndex)
  }

  const completeTour = () => {
    completeTourProgress('cards')
    onTourCompleted()
  }

  const highlightStep = (step: DriveStep, stepIndex: number) => {
    persistStep(stepIndex)
    driverObj.highlight(step)
  }

  const swipeBottomStep: DriveStep = {
    element: cardsTourSelectors.getTopGameCard,
    popover: {
      title: t('Swipe down'),
      description: t('Swipe down hint'),
      showButtons: ['next', 'close'],
      onNextClick: completeTour,
    },
  }

  const swipeRightStep: DriveStep = {
    element: cardsTourSelectors.getTopGameCard,
    popover: {
      title: t('Swipe right'),
      description: t('Swipe right hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(swipeBottomStep, 7),
    },
  }

  const swipeLeftStep: DriveStep = {
    element: cardsTourSelectors.getTopGameCard,
    popover: {
      title: t('Swipe left'),
      description: t('Swipe left hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(swipeRightStep, 6),
    },
  }

  const flipStep: DriveStep = {
    element: cardsTourSelectors.getTopGameCard,
    popover: {
      title: t('Flip the card'),
      description: t('Flip card hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(swipeLeftStep, 5),
    },
  }

  const deleteStep: DriveStep = {
    element: cardsTourSelectors.getMenuButton(deleteAriaLabel),
    disableActiveInteraction: true,
    popover: {
      title: t('Delete card'),
      description: t('Delete card hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(flipStep, 4),
    },
  }

  const reverseStep: DriveStep = {
    element: cardsTourSelectors.getMenuButton(reverseAriaLabel),
    disableActiveInteraction: true,
    popover: {
      title: t('Reverse sides'),
      description: t('Reverse sides hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(deleteStep, 3),
    },
  }

  const refreshStep: DriveStep = {
    element: cardsTourSelectors.getMenuButton(refreshAriaLabel),
    disableActiveInteraction: true,
    popover: {
      title: t('Refresh stack'),
      description: t('Refresh stack hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(reverseStep, 2),
    },
  }

  const editStep: DriveStep = {
    element: cardsTourSelectors.getMenuButton(editAriaLabel),
    disableActiveInteraction: true,
    popover: {
      title: t('Edit card'),
      description: t('Edit card hint'),
      showButtons: ['next', 'close'],
      onNextClick: () => highlightStep(refreshStep, 1),
    },
  }

  const steps = [
    editStep,
    refreshStep,
    reverseStep,
    deleteStep,
    flipStep,
    swipeLeftStep,
    swipeRightStep,
    swipeBottomStep,
  ]

  return { highlightStep, steps }
}
